/**
 * Repurpose.jsx
 * One thing in, copy for everywhere out.
 *
 * The work this saves is real: writing the same idea eight ways for eight
 * audiences is most of an afternoon. What it must not do is imply that
 * Kreeboom will then post them. It publishes to Facebook and Instagram and
 * nowhere else, so the channels are split on screen into the two it can carry
 * and the six it can only write, each with the reason attached.
 *
 * Two spends, both behind buttons: transcribing a file, and generating. The
 * transcript is editable in between, so a misheard product name is corrected
 * once rather than propagating into every version.
 *
 * There is a second way in, on its own tab: start from nothing but a sentence
 * and have the video made too. That one runs as a chain on the server rather
 * than here, because it takes minutes and must survive this tab closing — see
 * components/repurpose/FromPrompt.jsx and services/pipeline.js.
 *
 * Dependencies: services/api.js, components/repurpose/*, hooks/useBrands.jsx
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../services/api.js";
import { useBrands, useSelectedBrand } from "../hooks/useBrands.jsx";
import { Fanout, Film, Sparkle, Warning } from "../components/Icons.jsx";
import NoBrands from "../components/NoBrands.jsx";
import Source from "../components/repurpose/Source.jsx";
import Channels from "../components/repurpose/Channels.jsx";
import Output, { WrittenBy } from "../components/repurpose/Output.jsx";
import FromPrompt from "../components/repurpose/FromPrompt.jsx";

const DEFAULT_CHANNELS = [
  "instagram_reel",
  "facebook_post",
  "tiktok",
  "linkedin",
  "x_thread",
];

const money = (pence) => `£${(pence / 100).toFixed(2)}`;

export default function Repurpose() {
  const { brands, loading: brandsLoading } = useBrands();
  const [brandId] = useSelectedBrand();

  // Which way in. 'have' is something that already exists; 'make' starts from
  // a prompt and has the video made too.
  const [way, setWay] = useState("have");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  // The source, held here until a job exists on the server.
  const [mode, setMode] = useState("text");
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [mediaId, setMediaId] = useState(null);
  const [text, setText] = useState("");
  const [transcribed, setTranscribed] = useState(false);

  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [channels, setChannels] = useState(DEFAULT_CHANNELS);
  // What each channel is and whether this particular source could become a
  // draft of it. Recomputed by the server once a source exists, because a
  // carousel needs six pictures and one video is not six.
  const [cards, setCards] = useState([]);

  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [saved, setSaved] = useState({});
  const [missing, setMissing] = useState([]);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await api.repurpose.get(brandId);
      setData(res);
      setCards((current) => (current.length ? current : res.channels));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  // A different brand is a different library, a different set of connections
  // and different drafts. Keeping the source across that would offer to save a
  // Facebook draft against a page it was never written for.
  useEffect(() => {
    setJobId(null);
    setJob(null);
    setMediaId(null);
    setText("");
    setTitle("");
    setGoal("");
    setTranscribed(false);
    setSaved({});
    setMissing([]);
    setCards([]);
  }, [brandId]);

  if (!brandsLoading && brands.length === 0) return <NoBrands />;

  const limits = data?.limits ?? {
    maxSourceChars: 12000,
    minSourceChars: 80,
    maxChannels: 8,
  };

  const ready =
    text.trim().length >= limits.minSourceChars &&
    channels.length > 0 &&
    (mode === "text" || Boolean(mediaId));

  /** The job is created on first use and reused after that. */
  async function ensureJob() {
    if (jobId) return jobId;
    const res = await api.repurpose.start({
      brandId,
      sourceKind: mode,
      mediaId,
      title,
      sourceText: text,
      goal,
    });
    setJobId(res.job.id);

    // Now that the source is known, ask what it could honestly become.
    const full = await api.repurpose.job(res.job.id);
    setCards(full.channels);

    return res.job.id;
  }

  async function transcribe() {
    setTranscribing(true);
    setError(null);
    setNote(null);
    try {
      const id = await ensureJob();
      const res = await api.repurpose.transcribe(id);
      setText(res.text);
      setTranscribed(true);
      setNote(
        res.truncated
          ? `Transcribed, and trimmed to the first ${limits.maxSourceChars.toLocaleString()} characters. That cost about ${money(res.costPence)}.`
          : `Transcribed. That cost about ${money(res.costPence)}. Read it through and fix anything it misheard before generating.`,
      );
    } catch (e) {
      setError(e);
    } finally {
      setTranscribing(false);
    }
  }

  async function generate() {
    setGenerating(true);
    setError(null);
    setNote(null);
    setSaved({});
    try {
      const id = await ensureJob();
      // The source may have been edited since the job was made — a corrected
      // transcript, or more pasted in. Send it before spending on it.
      await api.repurpose.editSource(id, text);

      const res = await api.repurpose.generate(id, channels);
      const full = await api.repurpose.job(id);

      setJob(full.job);
      setCards(full.channels);
      setMissing(res.missing ?? []);
      setNote(
        `Written for ${Object.keys(res.outputs).length} places. That cost about ${money(res.costPence)}.`,
      );
      load();
    } catch (e) {
      setError(e);
    } finally {
      setGenerating(false);
    }
  }

  async function draft(channel) {
    setDrafting(channel);
    setError(null);
    try {
      const res = await api.repurpose.draft(jobId, channel);
      setSaved((s) => ({ ...s, [channel]: res.postId }));
      setNote(
        res.needsMedia
          ? "Saved to Drafts. Instagram will not publish without a picture, so attach one before approving it."
          : "Saved to Drafts, waiting for approval like any other post.",
      );
    } catch (e) {
      setError(e);
    } finally {
      setDrafting(null);
    }
  }

  function toggle(key) {
    setChannels((c) =>
      c.includes(key)
        ? c.filter((k) => k !== key)
        : c.length < limits.maxChannels
          ? [...c, key]
          : c,
    );
  }

  async function chooseMedia(id) {
    setMediaId(id);
    // A new source is a new job. Reusing the old one would leave copy written
    // from last week's video sitting under this week's.
    setJobId(null);
    setJob(null);
    setSaved({});
    setMissing([]);
    setTranscribed(false);
    setText("");
  }

  const written = job?.outputs ?? {};
  // Shown in the order they were asked for, not the order JSON came back in.
  const order = (job?.channels ?? []).filter((key) => written[key]);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Make</div>
          <h1>
            <span className="copilot-mark">
              <Fanout size={26} />
            </span>
            Repurpose
          </h1>
          <p>
            One video, article or picture, written up for every place you post.
            Kreeboom can carry two of them itself; the rest it writes for you to
            paste across.
          </p>

          <div className="rp-modes rp-ways" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={way === "have"}
              className={way === "have" ? "is-on" : ""}
              onClick={() => setWay("have")}
            >
              <Fanout size={15} /> Something I have
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={way === "make"}
              className={way === "make" ? "is-on" : ""}
              onClick={() => setWay("make")}
            >
              <Film size={15} /> From a prompt
            </button>
          </div>
        </div>
        {data && way === "have" && (
          <div className="page-head__actions">
            <span className="muted">
              {money(data.spend.monthPence)} of {money(data.spend.capPence)}{" "}
              used this month
            </span>
          </div>
        )}
      </div>

      {way === "make" ? (
        <FromPrompt brandId={brandId} onDrafted={load} />
      ) : (
        <>
          {error && (
            <p className="rp-error">
              <Warning size={14} /> {error.message}
            </p>
          )}
          {note && <p className="rp-note">{note}</p>}

          {loading && !data ? (
            <p className="muted">Loading…</p>
          ) : (
            <div className="rp-layout">
              <Source
                brandId={brandId}
                library={data?.library ?? []}
                limits={limits}
                title={title}
                onTitle={setTitle}
                goal={goal}
                onGoal={setGoal}
                mode={mode}
                onMode={(m) => {
                  setMode(m);
                  chooseMedia(null);
                }}
                mediaId={mediaId}
                onMedia={chooseMedia}
                text={text}
                onText={(v) => {
                  setText(v);
                  setTranscribed(false);
                }}
                transcribed={transcribed}
                onTranscribe={transcribe}
                transcribing={transcribing}
                onUploaded={async (id) => {
                  await load();
                  chooseMedia(id);
                }}
              />

              <div className="rp-right">
                <Channels
                  channels={cards}
                  chosen={channels}
                  onToggle={toggle}
                  max={limits.maxChannels}
                />

                <div className="rp-go card">
                  <button
                    type="button"
                    className="btn btn--primary btn--wide"
                    onClick={generate}
                    disabled={!ready || generating}
                  >
                    <Sparkle size={16} />
                    {generating
                      ? "Writing…"
                      : `Write ${channels.length} versions`}
                  </button>
                  <p className="muted">
                    One AI call, costing pennies. Everything written comes from
                    the source above and nothing else.
                  </p>
                </div>
              </div>
            </div>
          )}

          {Object.keys(written).length > 0 && (
            <section className="rp-results">
              <header className="rp-results__head">
                <h2>Written</h2>
                <WrittenBy model={job.model} at={job.updatedAt} />
              </header>

              {missing.length > 0 && (
                <p className="rp-error">
                  <Warning size={14} /> Nothing came back for{" "}
                  {missing
                    .map((k) => cards.find((c) => c.key === k)?.label ?? k)
                    .join(", ")}
                  . Generate again to try those.
                </p>
              )}

              <div className="rp-outputs">
                {order.map((key) => (
                  <Output
                    key={key}
                    channel={
                      cards.find((c) => c.key === key) ?? {
                        key,
                        label: key,
                        maxChars: 2200,
                      }
                    }
                    output={written[key]}
                    text={job.text?.[key] ?? ""}
                    onDraft={draft}
                    drafting={drafting === key}
                    savedPostId={saved[key]}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
